import { Check, Languages, LogOut, Monitor, Moon, Sun, SunMoon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useChangeTheme } from "@/hooks/use-change-theme";
import { useChangeLanguage } from "@/hooks/use-change-language";
import type { AppLanguage } from "@/i18n";

export interface OnboardingUserMenuUser {
  name: string;
  email: string;
  avatar?: string;
}

/**
 * Standalone account avatar + dropdown for the onboarding shell. Signals that
 * the user already has an account and lets them switch theme/language or sign
 * out mid-flow — the same intent as the app sidebar's NavUser, but freed from
 * the sidebar primitives so onboarding can double as "create another org".
 */
export function OnboardingUserMenu({
  user,
  onSignOut,
}: {
  user: OnboardingUserMenuUser;
  onSignOut: () => void;
}) {
  const { theme, changeTheme } = useChangeTheme();
  const { language, changeLanguage } = useChangeLanguage();
  const { t } = useTranslation("common");

  const initials =
    user.name
      ?.split(" ")
      .map((word) => word[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() ||
    user.email?.[0]?.toUpperCase() ||
    "?";

  const themeOptions: {
    value: "light" | "dark" | "system";
    label: string;
    icon: typeof Sun;
  }[] = [
    { value: "light", label: t("theme.light"), icon: Sun },
    { value: "dark", label: t("theme.dark"), icon: Moon },
    { value: "system", label: t("theme.system"), icon: Monitor },
  ];

  const languageOptions: { value: AppLanguage; label: string }[] = [
    { value: "sv", label: t("language.sv") },
    { value: "en", label: t("language.en") },
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="rounded-full outline-none ring-offset-background transition focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[state=open]:ring-2 data-[state=open]:ring-ring"
          aria-label={user.name || user.email}
        >
          <Avatar className="h-9 w-9">
            <AvatarImage src={user.avatar} alt={user.name} />
            <AvatarFallback className="bg-muted text-xs font-medium text-foreground">
              {initials}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-56 rounded-lg" align="end" sideOffset={8}>
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="grid gap-0.5 px-2 py-1.5 text-left">
            <span className="truncate text-sm font-semibold">{user.name}</span>
            <span className="truncate text-xs text-muted-foreground">
              {user.email}
            </span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <SunMoon />
              {t("appearance")}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {themeOptions.map((option) => {
                const Icon = option.icon;
                const isActive = theme === option.value;
                return (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={() => changeTheme(option.value)}
                  >
                    <Icon />
                    {option.label}
                    {isActive && <Check className="ml-auto size-4" />}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Languages />
              {t("language.label")}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {languageOptions.map((option) => {
                const isActive = language === option.value;
                return (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={() => void changeLanguage(option.value)}
                  >
                    {option.label}
                    {isActive && <Check className="ml-auto size-4" />}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onSignOut()}>
          <LogOut />
          {t("signOut")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
